import React, { useCallback, useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { showToast } from "services/utils/Status";
import { loadLoginAccess, storeLoginAccess } from "services/utils/loginAccess";
import { userManagementError } from "services/constants/ErrorMessages";
import { Loader } from "components/Loader";
import SearchIcon from "@mui/icons-material/Search";
import LoginIcon from "@mui/icons-material/Login";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { getAccessBasedRoute } from "services/utils/accessedBasedRoute";
import { supportLabel, adminRoleName } from "services/constants";
import "./Login.css";
import { tokens, headingSx, secondaryButtonSx } from "./brandTokens";
import {
  AuthLayout,
  AuthLeftPanel,
  AuthRightPanel,
  AuthCard,
  FeatureTimeline,
} from "./AuthShell";
import {
  parseJsonSafely,
  responseErrorMessage,
  BackendRequestError,
} from "./authErrors";

const WORKSPACE_ERROR = "Couldn't load your workspace. Please try again.";
const SIGN_IN_ERROR =
  "Couldn't sign in. Please check your details and try again.";
const ACCOUNT_NOT_FOUND_ERROR =
  "We couldn't find an account with those details. Please check and try again.";

const keys = ["domain_name", "firm_name"];

export const TenantDetails = ({
  setIsLoading,
  groupButtonActiveTap,
  tenantDetails,
  setShowTenantDetails,
}) => {
  const [searchTenatDetails, setSearchTenatDetails] = useState([]);
  const [globalSearchValue, setGlobalSearchValue] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("");

  const navigateToDomain = (row) => {
    setSelectedDomain(row.domain_name);
    let domainUrl = "";
    let domain = "";
    if (row.domain_name === "localhost" || row.domain_name === "127.0.0.1") {
      domainUrl = `http://${row.domain_name}:8000/api/`;
      domain = `http://${row.domain_name}:3000`;
    } else {
      domainUrl = `https://${row.domain_name}/api/`;
      domain = `https://${row.domain_name}`;
    }
    localStorage.clear();
    onSubmit(row, domainUrl, domain);
  };

  const getCompanyDetails = useCallback(
    async (token, domainUrl, domain, data, type) => {
      try {
        const response = await fetch(`${domainUrl}company/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        const resData = await parseJsonSafely(response);
        const urlpath = reDirectPath(resData, type);
        if (response.ok) {
          if (resData.length === 0) {
            let url =
              groupButtonActiveTap === "Email"
                ? `${domain}${urlpath}?token=${encodeURIComponent(
                    token,
                  )}&email=${data.email.toLowerCase()}`
                : `${domain}${urlpath}?token=${encodeURIComponent(
                    token,
                  )}&ph_no=${data.phonenumber}`;
            window.location.href = url;
          } else {
            let url =
              groupButtonActiveTap === "Email"
                ? `${domain}${urlpath}?token=${encodeURIComponent(
                    token,
                  )}&email=${data.email.toLowerCase()}`
                : `${domain}${urlpath}?token=${encodeURIComponent(
                    token,
                  )}&ph_no=${data.phonenumber}`;
            window.location.href = url;
          }
        } else {
          throw new BackendRequestError(
            responseErrorMessage(response, resData, WORKSPACE_ERROR),
          );
        }
      } catch (error) {
        console.error("[TenantDetails] company details request failed:", error);
        showToast(
          "error",
          error instanceof BackendRequestError ? error.message : WORKSPACE_ERROR,
        );
      }
    },
    [groupButtonActiveTap],
  );

  const onSubmit = useCallback(
    async (data, domainUrl, domain) => {
      try {
      let url =
        groupButtonActiveTap === "Email"
          ? `${domainUrl}login/email_verfication/?email=${data.email.toLowerCase()}`
          : `${domainUrl}phone_verfication/?phone_no=${data.phonenumber}`;
      const emailResponse = await fetch(url, {
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      });
      const emailReturnResponse = await parseJsonSafely(emailResponse);
      if (
        emailResponse.status === 200 &&
        emailReturnResponse.user_status === "Active"
      ) {
        localStorage.clear();
        function getCSRFToken() {
          return document.cookie
            .split("; ")
            .find((row) => row.startsWith("csrftoken="))
            ?.split("=")[1];
        }

        const isLocalhost =
          window.location.hostname === "localhost" ||
          window.location.hostname.startsWith("127.");

        let userResponse;
        const userId = emailReturnResponse.user_id;

        if (!isLocalhost) {
          await fetch(`${domainUrl}csrf/`, { credentials: "include" });

          userResponse = await fetch(`${domainUrl}custom-token-auth/`, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-CSRFToken": getCSRFToken(),
            },
            body: JSON.stringify({
              username: emailReturnResponse.emailuser.username,
              password: data.tenant_password,
            }),
          });
        } else {
          userResponse = await fetch(`${domainUrl}custom-token-auth/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              username: emailReturnResponse.emailuser.username,
              password: data.tenant_password,
            }),
          });
        }
        const userReturnResponse = await parseJsonSafely(userResponse);
        if (userResponse.status !== 200) {
          showToast(
            "error",
            responseErrorMessage(userResponse, userReturnResponse, SIGN_IN_ERROR),
          );
          setSelectedDomain("");
        } else {
          const userData = {
            id: userId,
            first_name: emailReturnResponse.emailuser.first_name,
            username: emailReturnResponse.emailuser.username,
            last_name: emailReturnResponse.emailuser.last_name,
            email: emailReturnResponse.emailuser.email,
            type: emailReturnResponse.type,
            phonenumber: emailReturnResponse.type.phonenumber,
            approver: emailReturnResponse.approver,
            teamlead: emailReturnResponse.teamlead,
            firmName: emailReturnResponse.firm_name,
            approver_id: emailReturnResponse.approver_id,
            image_url: emailReturnResponse.image_url,
            department_id: emailReturnResponse.department_id,
            department_name: emailReturnResponse.department_name,
            employee_id: emailReturnResponse.employee_id,
            designation: emailReturnResponse.designation,
          };
          const access = await loadLoginAccess({
            domain: domainUrl,
            token: userReturnResponse.token,
            userId,
            userType: userData.type,
          });
          storeLoginAccess(access);
          localStorage.setItem("currentuser", JSON.stringify(userData));
          localStorage.setItem("token", userReturnResponse.token);
          localStorage.setItem(
            "sessionToken",
            userReturnResponse.session_token,
          );

          getCompanyDetails(
            userReturnResponse.token,
            domainUrl,
            domain,
            data,
            emailReturnResponse.type,
          );
        }
      } else if (
        emailReturnResponse.exists === false ||
        emailResponse.status === 400
      ) {
        showToast(
          "error",
          responseErrorMessage(
            emailResponse,
            emailReturnResponse,
            ACCOUNT_NOT_FOUND_ERROR,
          ),
        );
        setShowTenantDetails(false);
        setIsLoading(false);
      } else if (emailReturnResponse.user_status === "Inactive") {
        showToast(
          "error",
          responseErrorMessage(
            emailResponse,
            emailReturnResponse,
            userManagementError.emailReturnResponseError,
          ),
        );
        setShowTenantDetails(false);
        setIsLoading(false);
      } else {
        // Any other status (403, 500, "no portal access", ...) used to fall
        // through with no feedback at all.
        showToast(
          "error",
          responseErrorMessage(emailResponse, emailReturnResponse, SIGN_IN_ERROR),
        );
        setSelectedDomain("");
      }
      } catch (error) {
        console.error("[TenantDetails] workspace sign in failed:", error);
        setSelectedDomain("");
        showToast("error", WORKSPACE_ERROR);
      }
    },
    [
      getCompanyDetails,
      groupButtonActiveTap,
      setIsLoading,
      setShowTenantDetails,
    ],
  );

  const reDirectPath = (companyData, type) => {
    let accessibleProducts = JSON.parse(localStorage.getItem("productAccess"));
    let product_name = accessibleProducts
      ? [adminRoleName, supportLabel].includes(type) &&
        companyData?.length === 0
        ? "Administration"
        : accessibleProducts[0]?.product_name
      : "default";
    const accesbasedReroutePath = getAccessBasedRoute(product_name);
    return accesbasedReroutePath;
  };

  useEffect(() => {
    if (tenantDetails.length === 1) {
      let row = tenantDetails[0];
      let domainUrl = "";
      let domain = "";
      if (row.domain_name === "localhost" || row.domain_name === "127.0.0.1") {
        domainUrl = `http://${row.domain_name}:8000/api/`;
        domain = `http://${row.domain_name}:3000`;
      } else {
        domainUrl = `https://${row.domain_name}/api/`;
        domain = `https://${row.domain_name}`;
      }
      onSubmit(row, domainUrl, domain);
    }
  }, [onSubmit, tenantDetails]);

  useEffect(() => {
    if (tenantDetails?.length !== 0 || tenantDetails?.length === 0) {
      const filteredResults = tenantDetails?.filter((item) =>
        keys.some((key) =>
          item[key]
            ?.toString()
            .toLowerCase()
            .includes(globalSearchValue.toString().toLowerCase()),
        ),
      );
      setSearchTenatDetails(filteredResults);
    }
  }, [globalSearchValue, tenantDetails]);

  const handleSignOut = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/auth/login";
  };

  const workspaceCount = searchTenatDetails?.length || 0;
  const totalCount = tenantDetails?.length || 0;
  const accentColors = ["#dbf3ef", "#e2efff", "#faedd4", "#fae5ee", "#ece9ff"];
  const accentTextColors = [
    "#2a8a72",
    "#2e6fbe",
    "#9f6510",
    "#aa4568",
    "#5b58d9",
  ];

  const workspaceFeatures = [
    {
      icon: <LoginIcon sx={{ fontSize: 22 }} />,
      title: "Auto-login for single domains",
      subtitle: "If you're linked to only one domain, we take you straight in.",
    },
    {
      icon: <SyncAltIcon sx={{ fontSize: 22 }} />,
      title: "Switch anytime",
      subtitle: "You can switch between your workspaces from within the app.",
    },
    {
      icon: <ShieldOutlinedIcon sx={{ fontSize: 22 }} />,
      title: "Isolated & secure",
      subtitle:
        "Each workspace is fully separate with its own data and permissions.",
    },
  ];

  return tenantDetails.length === 1 ? (
    <Loader info="Loading..." />
  ) : (
    <React.Fragment>
      <AuthLayout>
        <AuthLeftPanel
          top={
            <Mui.Stack spacing={4}>
              <Mui.Box sx={{ maxWidth: 520 }}>
                <Mui.Typography
                  sx={{
                    fontSize: { md: 32, lg: 40, xl: 46 },
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Choose your
                  <br />
                  workspace
                </Mui.Typography>
                <Mui.Typography
                  sx={{
                    mt: 2.5,
                    fontSize: { md: 15, lg: 17 },
                    lineHeight: 1.5,
                    color: "rgba(255,255,255,0.72)",
                    maxWidth: 460,
                  }}
                >
                  You have access to multiple organisations. Select the one
                  you&apos;d like to work in today.
                </Mui.Typography>
              </Mui.Box>

              <FeatureTimeline items={workspaceFeatures} />
            </Mui.Stack>
          }
          bottom={
            <Mui.Box sx={{ fontSize: 12, color: "rgba(255,255,255,0.62)" }}>
              © 2024 String Consultants. All Rights Reserved
            </Mui.Box>
          }
        />

        <AuthRightPanel>
          <AuthCard
            maxWidth={{ xs: "100%", sm: 560, md: 520, lg: 600, xl: 640 }}
            sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 64px)",
              overflow: "hidden",
            }}
          >
            <Mui.Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 4.5 }, pb: 3 }}>
              <Mui.Typography sx={headingSx}>Select a domain</Mui.Typography>
              <Mui.Typography
                sx={{
                  mt: 1.25,
                  fontSize: 15,
                  lineHeight: 1.45,
                  color: "rgba(21,23,26,0.7)",
                }}
              >
                You have access to <b>{totalCount} workspaces.</b> Pick one to
                continue.
              </Mui.Typography>

              <Mui.Box sx={{ mt: 3 }}>
                <Mui.Card
                  component="form"
                  sx={{
                    p: "8px 14px",
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    borderRadius: "14px",
                    boxShadow: "none",
                    border: "1px solid rgba(21,23,26,0.16)",
                  }}
                >
                  <SearchIcon sx={{ color: "rgba(21,23,26,0.5)", mr: 1.25 }} />
                  <Mui.InputBase
                    sx={{ flex: 1, fontSize: 15, color: "#15171a" }}
                    placeholder="Search by name or domain..."
                    inputProps={{
                      "aria-label": "Search by name or domain",
                    }}
                    value={globalSearchValue}
                    onChange={(e) => setGlobalSearchValue(e.target.value)}
                  />
                </Mui.Card>
              </Mui.Box>
            </Mui.Box>

            <Mui.Box
              sx={{
                borderTop: "1px solid rgba(21,23,26,0.1)",
                borderBottom: "1px solid rgba(21,23,26,0.1)",
                px: { xs: 3, sm: 4 },
                py: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Mui.Typography
                sx={{
                  fontSize: 13,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  color: "rgba(21,23,26,0.55)",
                }}
              >
                Workspaces
              </Mui.Typography>
              <Mui.Box
                sx={{
                  borderRadius: 999,
                  background: "#f3efe7",
                  px: 1.5,
                  py: 0.5,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "rgba(21,23,26,0.62)",
                  lineHeight: 1,
                }}
              >
                {workspaceCount} OF {totalCount}
              </Mui.Box>
            </Mui.Box>

            <Mui.Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                px: { xs: 3, sm: 4 },
                py: 2,
              }}
            >
              <Mui.Stack spacing={1.5} sx={{ width: "100%" }}>
                {searchTenatDetails?.length === 0 ? (
                  <Mui.Alert severity="info">No data available</Mui.Alert>
                ) : (
                  searchTenatDetails.map((row, index) => (
                    <Mui.Box
                      key={`${row.domain_name}-${index}`}
                      onClick={() => navigateToDomain(row)}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "14px",
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#faf8f4",
                        },
                      }}
                    >
                      <Mui.Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          background: accentColors[index % accentColors.length],
                          color:
                            accentTextColors[index % accentTextColors.length],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 15,
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {row.firm_name
                          ?.split(" ")
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((part) => part.charAt(0).toUpperCase())
                          .join("") || "WS"}
                      </Mui.Box>
                      <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
                        <Mui.Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: "#15171a",
                          }}
                          noWrap
                        >
                          {row.firm_name}
                        </Mui.Typography>
                        <Mui.Typography
                          sx={{
                            mt: 0.25,
                            fontSize: 13.5,
                            color: "rgba(21,23,26,0.56)",
                          }}
                          noWrap
                        >
                          {row.domain_name}
                        </Mui.Typography>
                      </Mui.Box>
                      <Mui.Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          border:
                            selectedDomain === row.domain_name
                              ? `2px solid ${tokens.primary}`
                              : "2px solid rgba(21,23,26,0.22)",
                          background:
                            selectedDomain === row.domain_name
                              ? `radial-gradient(circle, ${tokens.primary} 0 45%, transparent 46%)`
                              : "transparent",
                          flexShrink: 0,
                        }}
                      />
                    </Mui.Box>
                  ))
                )}
              </Mui.Stack>
            </Mui.Box>

            <Mui.Box
              sx={{
                px: { xs: 3, sm: 4 },
                pt: 2.5,
                pb: 3,
                borderTop: "1px solid rgba(21,23,26,0.1)",
              }}
            >
              <Mui.Button
                variant="outlined"
                size="large"
                sx={{ ...secondaryButtonSx, width: "100%" }}
              >
                Select a workspace to continue
              </Mui.Button>
              <Mui.Typography
                sx={{
                  mt: 2,
                  textAlign: "center",
                  fontSize: 13.5,
                  color: "rgba(21,23,26,0.56)",
                  cursor: "pointer",
                }}
                onClick={handleSignOut}
              >
                Not you? Sign out
              </Mui.Typography>
            </Mui.Box>
          </AuthCard>
        </AuthRightPanel>
      </AuthLayout>
    </React.Fragment>
  );
};
